import { NavigationHeight } from '@/components/Navigation/NavigationHelpers';
import ResourcesSidebar from '@/components/ResourcesSidebar/ResourcesSidebar';
import {
  FilterSchema,
  InitialFilterValues,
  useSectionsConfig,
} from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import {
  parseFiltersFromSearchParams,
  updateSearchParamsWithFilters,
} from './ResourcePageHelpers';
import { styled } from '@mui/material';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router';
import FilesAccessibilityInfoBanner, {
  shouldShowFilesAccessibilityBanner,
} from '@/components/FilesAccessibilityInfoBanner/FilesAccessibilityInfoBanner';
import { GameTileWidth } from '@/components/GameCard/GameCard';
import {
  ScrollContainerContext,
  ScrollContainerRef,
} from '@/contexts/ScrollContainerContext';

const ResourcesPage = () => {
  const visibleSections = useSectionsConfig();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const filters = useMemo<Partial<FilterSchema>>(
    () => parseFiltersFromSearchParams(searchParams),
    [searchParams],
  );

  const forceOpenReleaseDate =
    searchParams.get('expand_release_date') === '1';

  const handleFiltersChange = useCallback(
    (newFilters: FilterSchema) => {
      const next = updateSearchParamsWithFilters(
        searchParams,
        newFilters as Partial<FilterSchema>,
      );
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const initialValues = useMemo<
    InitialFilterValues | undefined
  >(() => {
    if (!filters || Object.keys(filters).length === 0) {
      return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { navigation, ...rest } = filters;
    return rest;
  }, [filters]);

  return (
    <Root data-sidebar-open={isSidebarOpen}>
      <ResourcesSidebar
        onFiltersChange={handleFiltersChange}
        visibleSections={visibleSections}
        initialValues={initialValues}
        onOpenChange={setIsSidebarOpen}
        forceOpenReleaseDate={forceOpenReleaseDate}
      />
      <ResourcesContent filters={filters} />
    </Root>
  );
};

type ResourcesContentProps = {
  filters: Partial<FilterSchema>;
};

const ResourcesContent = memo(
  ({ filters }: ResourcesContentProps) => {
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const { pathname } = useLocation();
    const showFilesAccessibilityBanner =
      shouldShowFilesAccessibilityBanner(pathname);

    return (
      <ScrollContainerContext.Provider
        value={scrollContainerRef as unknown as ScrollContainerRef}
      >
        <div
          ref={scrollContainerRef}
          className="resources-page-wrapper wide-scrollbar"
        >
          <div className="resources-content">
            {showFilesAccessibilityBanner && (
              <FilesAccessibilityInfoBanner />
            )}
            <div className="page-width-wrapper">
              <Outlet context={filters} />
            </div>
          </div>
        </div>
      </ScrollContainerContext.Provider>
    );
  },
);

const Root = styled('div')({
  position: 'relative',
  width: '100%',
  background: '#F9FAFB',
  '& .resources-page-wrapper': {
    height: `calc(100vh - ${NavigationHeight}px)`,
    overflowY: 'auto',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },

  '& .resources-content': {
    width: '100%',
    transition: 'padding-left 0.25s',
    paddingLeft: 0,
    // Grow to the full scrollport so a page that opts in (see BasketBar's
    // sticky variant) can reach its bottom edge; never shrink below content.
    flex: '1 0 auto',
    display: 'flex',
    flexDirection: 'column',
  },

  '& .resources-content > .page-width-wrapper': {
    flex: '1 0 auto',
    display: 'flex',
    flexDirection: 'column',
  },
  '&[data-sidebar-open="true"] .resources-content': {
    paddingLeft: GameTileWidth + 26,
  },
});

export default ResourcesPage;
